import { cn } from "@/lib/utils";
import "@assets/css/bootstrap-grid.css";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/ui/accordion";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@ui/external/components/checkbox";
import DragContainer from "@ui/internal/components/course-drag/DragContainer";

import { ErrorLabel, FormLabel } from "@ui/internal/components/label";
import { getInputField } from "@utils/function/index.function";
import {
  changeSymbolstoDash,
  isEditorContentEmpty,
  makeSlug,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import { CourseFormData, courseSchema } from "@utils/interface/form.schema";

import { useListData, usePostData } from "@utils/query/index.query";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Spinner } from "@components/ui/spinner";
import { Switch } from "@components/ui/switch";
import { useInfiniteScroller } from "@pages/dashboard/test/hook";
import { FormSkeleton } from "@ui/external/components/skeleton";
import DateTime from "@ui/internal/components/date-time";
import FileUpload from "@ui/internal/components/file-upload/FileUpload";
import { AsyncSelect } from "@ui/internal/components/formelement";
import { IndividualQuery } from "@utils/interface/index.interface";
import { CourseData, CourseState } from "@utils/interface/journey.interface";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import { Calendar, Pencil, Save, Trash, X } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useInView } from "react-intersection-observer";
import { CourseService } from "@services/CourseServices";
import axiosInstance from "@services/index.services";
import CheckboxInternal from "@ui/internal/components/checkbox";
type ItemDescription = {
  type: "description";
  id: number;
  question: string;
  answer: string;
};

type ItemQuotation = {
  type: "quotation";
  id: number;
  description: string;
};

type ItemImage = {
  type: "image";
  id: number;
  label: string;
  image: File | ""; // or string if you store URL or base64
};

export type Format = {
  items: (ItemDescription | ItemQuotation | ItemImage)[];
};

// Isolated component with local state so typing doesn't re-trigger the
// huge CourseForm render cycle and revert the input value.
const IconNameInput: React.FC<{
  initialName: string;
  isEditing: boolean;
  onNameChange: (name: string) => void;
}> = ({ initialName, isEditing, onNameChange }) => {
  const [localName, setLocalName] = React.useState(initialName);

  // When we enter editing mode, re-sync from the form so we start fresh.
  React.useEffect(() => {
    if (isEditing) setLocalName(initialName);
  }, [isEditing]); // intentionally NOT including initialName — we only want to sync on mode switch

  return (
    <input
      type="text"
      value={localName}
      placeholder="Enter icon name"
      readOnly={!isEditing}
      onChange={(e) => {
        setLocalName(e.target.value);
      }}
      onBlur={() => {
        onNameChange(localName);
      }}
      className={`flex-1 text-xs text-center p-1 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
        !isEditing ? "bg-gray-100 cursor-default" : ""
      }`}
    />
  );
};

type IconsFieldProps = {
  control: any;
  allScrolledData: any[];
  isFetchingNextPage: boolean;
  scrollRef: (node?: Element | null) => void;
  activeTab: "images" | "upload";
  setActiveTab: (tab: "images" | "upload") => void;
  editingIconId: number | null;
  setEditingIconId: (id: number | null) => void;
  savingIconId: number | null;
  setSavingIconId: (id: number | null) => void;
  handleDelete: (id: number) => void;
  refetch: () => void;
  pageItem: string | null;
  watch: any;
};

const IconsField: React.FC<IconsFieldProps> = ({
  control,
  allScrolledData,
  isFetchingNextPage,
  scrollRef,
  activeTab,
  setActiveTab,
  editingIconId,
  setEditingIconId,
  savingIconId,
  setSavingIconId,
  handleDelete,
  refetch,
  pageItem,
  watch,
}) => {
  // Track latest names to avoid stale closure when saving
  const nameRefs = React.useRef<Record<number, string>>({});

  return (
    <div className="flex flex-col gap-4 justify-start">
      <Dialog>
        <DialogTrigger className="flex justify-start w-full">
          <Button type="button">Add Icons</Button>
        </DialogTrigger>
        <DialogContent className="min-w-2xl w-full">
          <div className="flex gap-2 items-center">
            <Button
              type="button"
              onClick={() => {
                refetch();
                setActiveTab("images");
              }}
            >
              Select Images
            </Button>
            <Button type="button" onClick={() => setActiveTab("upload")}>
              Upload
            </Button>
          </div>
          <DialogHeader>
            <DialogTitle className="text-sm">
              {activeTab === "images" ? "Add Icons" : "Upload Icons"}
            </DialogTitle>

            {activeTab === "upload" ? (
              <DialogDescription asChild>
                <div className="pt-4">
                  <Controller
                    name={"icons"}
                    control={control}
                    render={({ field: { value, onChange, ...restFields } }) => (
                      <FileUpload
                        endpoint="/custom-gallery/bulk-create/"
                        additionalBody={{ is_icon: true }}
                        manualUpload={true}
                        value={value}
                        multiple={true}
                        uploadToCloud={true}
                        allowCopy={false}
                        onChange={onChange}
                        {...restFields}
                      />
                    )}
                  />
                </div>
              </DialogDescription>
            ) : (
              <div className="grid grid-cols-3 max-h-[400px] overflow-scroll gap-3">
                {allScrolledData?.map((item) => (
                  <div key={item?.id}>
                    <Controller
                      name="icons"
                      control={control}
                      render={({ field }) => {
                        const currentValue = Array.isArray(field.value)
                          ? field.value
                          : [];
                        const uploadedFiles = currentValue.filter(
                          (val: any) => val.file,
                        );
                        const selectedItems = currentValue.filter(
                          (val: any) => !val.file,
                        ) as (number | { id: number; name?: string | null })[];
                        const selectedItem = selectedItems.find((val) =>
                          typeof val === "object"
                            ? val.id === item.id
                            : val === item.id,
                        );
                        const isSelected = !!selectedItem;
                        const customName =
                          typeof selectedItem === "object"
                            ? selectedItem.name
                            : null;

                        return (
                          <>
                            <div className="flex gap-2 my-2 justify-between">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  let newSelectedItems;
                                  if (isSelected) {
                                    newSelectedItems = selectedItems.filter(
                                      (val) =>
                                        typeof val === "object"
                                          ? val.id !== item.id
                                          : val !== item.id,
                                    );
                                  } else {
                                    newSelectedItems = [
                                      ...selectedItems,
                                      {
                                        id: item.id,
                                        name: customName || item.name || null,
                                      },
                                    ];
                                  }
                                  field.onChange([
                                    ...newSelectedItems,
                                    ...uploadedFiles,
                                  ]);
                                }}
                              />
                              {!isSelected && (
                                <span
                                  onClick={() => handleDelete(item?.id)}
                                  title="Delete"
                                  className="cursor-pointer"
                                >
                                  <Trash size={13} className="text-red-700" />
                                </span>
                              )}
                            </div>
                            <img
                              src={item?.image}
                              className="w-full h-[150px] rounded-2xl object-cover"
                              alt="Gallery image"
                            />
                            <div className="flex items-center gap-2 mt-2">
                              <IconNameInput
                                initialName={customName ?? item?.name ?? ""}
                                isEditing={editingIconId === item.id}
                                onNameChange={(newName) => {
                                  nameRefs.current[item.id] = newName;
                                  const updatedItems = selectedItems.map(
                                    (val) => {
                                      if (
                                        typeof val === "object" &&
                                        val.id === item.id
                                      ) {
                                        return { ...val, name: newName || null };
                                      }
                                      return val;
                                    },
                                  );
                                  field.onChange([
                                    ...updatedItems,
                                    ...uploadedFiles,
                                  ]);
                                }}
                              />
                              {savingIconId === item.id ? (
                                <Spinner size="sm" />
                              ) : editingIconId === item.id ? (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    setSavingIconId(item.id);
                                    try {
                                      if (item.id) {
                                        const currentName = nameRefs.current[item.id];
                                        await axiosInstance.patch(
                                          `/custom-gallery/${item.id}/`,
                                          {
                                            name: currentName || item.name || "",
                                          },
                                        );
                                        await refetch();
                                        showToast({
                                          title: "Success",
                                          description: "Icon name saved successfully",
                                          type: "success",
                                        });
                                        setEditingIconId(null);
                                      }
                                    } catch {
                                      showToast({
                                        title: "Error",
                                        description: "Failed to save icon name",
                                        type: "error",
                                      });
                                    } finally {
                                      setSavingIconId(null);
                                    }
                                  }}
                                  className="p-1 text-green-600 hover:text-green-800"
                                  title="Save"
                                >
                                  <Save size={16} />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setEditingIconId(item.id)}
                                  className="p-1 text-blue-600 hover:text-blue-800"
                                  title="Edit"
                                >
                                  <Pencil size={16} />
                                </button>
                              )}
                            </div>
                          </>
                        );
                      }}
                    />
                  </div>
                ))}
                <div ref={scrollRef}></div>
                {isFetchingNextPage && (
                  <div className="flex h-12 items-center justify-center">
                    <Spinner />
                  </div>
                )}
              </div>
            )}
          </DialogHeader>
        </DialogContent>
      </Dialog>
      {watch("icons") ? watch("icons").length + " Selected" : ""}
    </div>
  );
};

const CourseForm: React.FC = () => {
  const { searchParams, handleAction, setSearchParams } = useHandleAction({});
  const queryClient = useQueryClient();
  /* avoid repetation */
  const pageItem = searchParams.get("page-item");
  const pageMode = searchParams.get("page");
  const pathname = usePathname();
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    register,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(courseSchema),
    defaultValues: {
      title: pageMode === "add" ? (searchParams.get("search") as string) : "",
      course_type: [{ label: "Default", value: "default" }],
      is_ai_integrated: false,
      // items: [
      //   {
      //     type: "description",
      //     id: 0,
      //     title: "",
      //     children: [
      //       {
      //         id: 0,
      //         question: "",
      //         answer: "",
      //       },
      //     ],
      //   },
      //   {
      //     type: "quotation",
      //     id: 1,
      //     title: "",
      //     description: "",
      //   },
      // ],
    },
  });

  // console.log("THIS IS THE COURSE FORM DATA", watch());
  // console.log("THIS IS THE ERRORS IN COURSE", errors);

  const { fields, append, move, remove } = useFieldArray({
    name: "items",
    control,
  });
  /* set default value */

  const { data, isPending: fetchPending } = useListData<
    IndividualQuery<CourseData>
  >({
    endpoint: endpoint["course"],
    id: pageItem || "",
    key: "course" + pageItem,
    enabled: pageItem ? true : false,
  });

  const { setIsRetrive }: any = usePending();
  const setIsFormPending = useSelectionStore((state) => state.setIsFormPending);
  const hasResetRef = useRef(false);

  useEffect(() => {
    hasResetRef.current = false;
  }, [pageItem, pageMode]);

  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);

    if (hasResetRef.current) return;

    if (pageMode == "add") {
      hasResetRef.current = true;
      reset({
        title: searchParams.get("search") || "",
        mentors_overview: "",
        course_type:
          pathname === "courses"
            ? [] // multi-select: no selection = default course
            : pathname === "see-courses"
              ? [{ label: "SEE", value: "see" }]
              : pathname === "plus2-courses"
                ? [{ label: "Plus Two", value: "plus_two" }]
                : pathname === "jrp-courses"
                  ? [{ label: "JRP", value: "jrp" }]
                  : [{ label: "Default", value: "default" }],
      });
      return;
    }

    if (data && pageMode !== "add") {
      hasResetRef.current = true;
      let newItems = [
        ...(typeof data?.data?.description === "string"
          ? JSON.parse(data?.data?.description)
          : data?.data?.description),
        ...(typeof data?.data?.quotation === "string"
          ? JSON.parse(data?.data?.quotation)
          : data?.data?.quotation),
      ];
      console.log("New Itemss", newItems);
      setDateTimeData(() => data?.data?.course_state ?? []);
      console.log("This is the data for reset", data);
      return reset({
        title: data?.data?.title,
        icons: data?.data?.icons?.map((item) => item?.id),
        syllabus_link: data?.data.syllabus_link,
        course_link: data?.data.course_link,
        cover_image: data?.data?.cover_image?.trim(),
        amount: data?.data?.amount,
        level: [{ value: data?.data?.level.id, label: data?.data?.level.name }],
        mentor: data.data.mentor.map((item) => ({
          value: item.id,
          label: item?.full_name,
        })),
        duration: [
          {
            value: data?.data?.duration.id,
            label: data?.data?.duration.duration,
          },
        ],
        categories: data?.data?.categories?.map((item) => ({
          label: item?.name,
          value: item?.id,
        })),
        currency: data?.data?.currency
          ? [
              {
                value: data?.data?.currency,
                label: data?.data?.currency,
              },
            ]
          : [],
        registration_fee: data?.data?.registration_fee,
        is_price: data?.data?.is_price,
        is_discount: data?.data?.is_discount || false,
        is_ai_integrated: data?.data?.is_ai_integrated || false,
        discount: data?.data?.discount,
        items: newItems,
        course_state:
          typeof data?.data?.course_state === "string"
            ? JSON.parse(data?.data?.course_state)
            : data?.data?.course_state,
        discount_type: data?.data?.discount_type
          ? [
              {
                value: data?.data?.discount_type,
                label: data?.data?.discount_type,
              },
            ]
          : [],
        header_code: isEditorContentEmpty(data?.data?.header_code)
          ? ""
          : data?.data?.header_code,
        footer_code: isEditorContentEmpty(data?.data?.footer_code)
          ? ""
          : data?.data?.footer_code,
        meta_title: isEditorContentEmpty(data?.data?.meta_title)
          ? ""
          : data?.data?.meta_title,
        meta_description: isEditorContentEmpty(data?.data?.meta_description)
          ? ""
          : data?.data?.meta_description,
        course_state_type_multi: (data?.data?.course_state_type_multi ?? []).map(
          (e: any) => e.id.toString(),
        ),

        learning_format: [
          {
            value: data?.data?.learning_format,
            label: data?.data?.learning_format,
          },
        ],
        slug: data?.data?.slug,
        excerpt: data?.data?.excerpt,
        feature_image: data?.data?.feature_image,
        featured_video: data?.data?.featured_video?.trim(),
        mentors_overview: data?.data?.mentors_overview || "",
        course_type:
          pathname == "courses"
            ? (() => {
                const selectedTypes: { label: string; value: string }[] = [];
                if (data?.data?.is_see) {
                  selectedTypes.push({ label: "SEE", value: "see" });
                }
                if (data?.data?.is_plus2) {
                  selectedTypes.push({ label: "Plus Two", value: "plus_two" });
                }
                if (data?.data?.is_corporate) {
                  selectedTypes.push({ label: "Corporate", value: "corporate" });
                }
                if (data?.data?.is_jrp) {
                  selectedTypes.push({ label: "JRP", value: "jrp" });
                }
                if (
                  selectedTypes.length === 0 &&
                  data?.data?.course_type &&
                  data.data.course_type !== "default"
                ) {
                  selectedTypes.push({
                    label:
                      data.data.course_type === "plus_two"
                        ? "Plus Two"
                        : data.data.course_type === "see"
                          ? "SEE"
                          : (data.data.course_type as string).charAt(0).toUpperCase() +
                            (data.data.course_type as string).slice(1),
                    value: data.data.course_type,
                  });
                }
                // Multi-select: empty selection represents a default course.
                return selectedTypes;
              })()
            : // course_type is saved as "default" for JRP (the backend enum only
              // accepts "default"/"see"), so read the flag instead of the enum
              // or the select would always show "Default" here. Scoped to the JRP
              // page so a course flagged both SEE and JRP still shows SEE on
              // /see-courses and does not get demoted on save.
              pathname == "jrp-courses" && data?.data?.is_jrp
              ? [{ label: "JRP", value: "jrp" }]
              : data?.data?.course_type
                ? [
                    {
                      label:
                        data.data.course_type === "plus_two"
                          ? "Plus Two"
                          : data.data.course_type === "see"
                            ? "SEE"
                            : data.data.course_type.charAt(0).toUpperCase() +
                              data.data.course_type.slice(1),
                      value: data.data.course_type,
                    },
                  ]
                : [{ label: "Default", value: "default" }],
      });
    }
  }, [data, fetchPending, reset, setIsRetrive, pageMode, pageItem]);

  // const { mutate: addCourse, isPending } = useMutation({
  //   mutationFn: CourseService,
  //   onSuccess: (res) => {
  //     queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
  //     queryClient.invalidateQueries({
  //       queryKey: [`${pathname}-listing-scroll`],
  //     });
  //     queryClient.invalidateQueries({ queryKey: ["course-section-listing"] });
  //     setIsFormPending(false);

  //     showToast({
  //       description: `Course ${res.status === 200 ? "Updated" : "Created"}`,
  //       type: "success",
  //       title: "Success",
  //     });

  //     handleAction({
  //       type: "page-close",
  //     });
  //   },
  //   onError: (error) => {
  //     console.log(error, "error00");
  //     setIsFormPending(false);
  //     throwErrorAlert(error);
  //   },
  // });
  const handleMutate = (data: any) => {
    data = {
      ...data,
      categories: data.categories?.map(
        (item: { label: string; value: string }) => item.value,
      ),
      icons: data?.icons?.map((icon: any) => typeof icon === 'object' ? icon.id : icon),
      // categories: data["categories"]?.[0]?.["value"],
      duration: data["duration"]?.[0]?.["value"],
      level: data["level"]?.[0]?.["value"],
      mentor: data?.mentor?.map((item: any) => item?.value),
      learning_format: data["learning_format"]?.[0]?.["value"],
      currency: data.currency?.[0]?.["value"],
      amount: data.amount,
      registration_fee: data.registration_fee,
      discount: data.discount,
      discount_type: data?.discount_type?.[0]?.value,
      course_state: JSON.stringify(dateTimeData),
      course_state_type_multi: data.course_state_type_multi?.map((e: string) =>
        Number(e),
      ),
      // quotation: JSON.stringify(
      //   data["items"]?.filter((item: any) => item.type === "quotation")
      // ),

      description: JSON.stringify(data["items"]),
      mentors_overview: data.mentors_overview || "",
    };

    const selectedCourseTypes = Array.isArray(data["course_type"])
      ? data["course_type"].map((item: any) => item?.value)
      : [];

    if (pathname == "courses") {
      // On the normal Courses page, derive flags from the multi-select dropdown.
      // Set explicitly both ways so selecting SEE/Plus Two turns the flag on AND
      // deselecting turns it off (otherwise the field is omitted and the backend
      // keeps its old value).
      data.is_see = selectedCourseTypes.includes("see");
      data.is_plus2 = selectedCourseTypes.includes("plus_two");
      data.is_corporate = selectedCourseTypes.includes("corporate");
      // JRP is set from here too, which is the only way to promote an existing
      // course: /jrp-courses lists is_jrp=true only, so a course that isn't
      // flagged yet can never be opened from there.
      data.is_jrp = selectedCourseTypes.includes("jrp");
      // The website gates SEE/Plus Two/Corporate purely on the is_see/is_plus2/
      // is_corporate booleans above. The backend course_type enum only accepts
      // "default"/"see" ("plus_two"/"corporate" are rejected), so keep course_type
      // to a valid value and let the booleans carry the real meaning.
      data.course_type = data.is_see ? "see" : "default";
    } else {
      // Dedicated course pages: derive the flags from the selected course type so a
      // course can be demoted (set to Default) here, not just promoted.
      const selectedType = data["course_type"]?.[0]?.value || "default";
      data.is_see = selectedType === "see";
      data.is_plus2 = selectedType === "plus_two";
      data.is_corporate = selectedType === "corporate";
      if (pathname == "masterclass-courses") {
        data.is_masterclass = true;
      }
      if (pathname == "jrp-courses") {
        data.is_jrp = true;
      }
      // Backend only accepts "default"/"see" for course_type.
      data.course_type = data.is_see ? "see" : "default";
    }

    delete data["items"];

    if (data.syllabus_link == null || data.syllabus_link == "null") {
      delete data["syllabus_link"];
    }
    if (data.course_link == null || data.course_link == "null") {
      delete data["course_link"];
    }
    // if (data.mentor.length == 0) {
    //   delete data["mentor"];
    // }

    // is_price is a display flag only: it decides whether the website renders the
    // price section, nothing else. Payments, invoices and enrolments keep reading
    // the stored amount, so the pricing fields are always sent regardless of the
    // toggle -- blanking them here is what zeroed already-issued invoices.
    // Only drop values the user genuinely left empty, so an unpriced course never
    // posts "" to a decimal column.
    (
      ["amount", "registration_fee", "discount", "discount_type", "currency"] as const
    ).forEach((field) => {
      const value = data[field];
      if (value === "" || value === undefined || value === null) {
        delete data[field];
      }
    });
    if (pageItem) {
      data = {
        ...data,
        id: pageItem,
      };
      // if (
      //   !(
      //     data.feature_image instanceof File ||
      //     data.feature_image instanceof Blob
      //   )
      // )
      //   delete data.feature_image;
      // if (
      //   !(
      //     data.featured_video instanceof File ||
      //     data.featured_video instanceof Blob
      //   )
      // )
      //   delete data.featured_video;
      // if (
      //   !(data.cover_image instanceof File || data.cover_image instanceof Blob)
      // )
      //   delete data.cover_image;
    }

    return {
      data,
      endpoint: endpoint["course"],
      method: pageItem ? METHODS.PATCH : METHODS.POST,
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      queryClient.invalidateQueries({ queryKey: ["course-section-listing"] });
      setIsFormPending(false);

      showToast({
        description: `Course ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "page-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    console.log(error, "error00");
    setIsFormPending(false);
    throwErrorAlert(error);
  };
  const { mutate } = usePostData();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const onSubmit = (data: CourseFormData) => {
    // setIsFormPending(true);
    // handleMutate falls back to POST whenever page-item is absent, so an edit
    // whose id went missing from the URL would silently create a duplicate
    // course carrying the whole loaded payload. Refuse instead of guessing.
    if (pageMode === "edit" && !pageItem) {
      return showToast({
        description:
          "Cannot save: this course's id is missing from the URL. Reopen the course from the list and try again.",
        title: "Edit target lost",
        type: "error",
      });
    }
    console.log("THIS IS THE DATA ON SUBMIT :LLLLL", data);
    // const status = validateData(data["items"]);
    // if (!status) {
    //   setIsFormPending(false);

    //   return showToast({
    //     description:
    //       "All Fields For Descriptions are not Filled check all entries for descriptions",
    //     title: "Some Fileds are missing",
    //     type: "warning",
    //   });
    // }

    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => {
        handleSuccess({ data, status });
      },
    });
  };
  const watchedTitle = watch("title");
  const watchedMentor = watch("mentor");
  const watchedIsPrice = watch("is_price");
  const watchedIsDiscount = watch("is_discount");

  useEffect(() => {
    if (pageMode !== "edit" && watchedTitle) {
      setValue("slug", changeSymbolstoDash(makeSlug(watchedTitle)));
    }
  }, [watchedTitle, pageMode, setValue]);

  const [dateTimeData, setDateTimeData] = useState<CourseState[]>([]);
  const [openModal, setOpenModal] = useState(false);

  const formSchema: FormField[] = useMemo(
    () => [
      {
        name: "title",
        label: "Course Title",
        colSpan: 12,
        type: "text",
        placeholder: "Enter Title",

        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "slug",
        label: "Course Slug",
        colSpan: 12,
        type: "text",
        placeholder: "Enter Slug",

        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "level",
        label: "Level",
        colSpan: 12,
        type: "async",
        placeholder: "Select Level",

        endpoint: endpoint["level"],
        format: { key: "name", value: "id", mutate: "name" },
        settings: {
          add: true,
          update: true,
          remove: true,
          creatable: false,
          debounce: 1000,
        },
        className: `mb-[20px] min-w-full !w-full`,
        visable: true,
      },
      {
        name: "course_type",
        label: "Course Type",
        colSpan: 12,
        type: "async",
        placeholder: "Select Course Type",
        format: { key: "name", value: "id" },
        option: [
          { label: "Default", value: "default" },
          { label: "SEE", value: "see" },
          { label: "Plus Two", value: "plus_two" },
          { label: "Corporate", value: "corporate" },
          { label: "JRP", value: "jrp" },
        ],
        className: `mb-[20px] min-w-full !w-full`,
        visable: true,
        isMulti: pathname == "courses",
      },
      {
        name: "mentor",
        label: "Mentor",
        colSpan: 12,
        type: "async",
        placeholder: "Select Mentor",

        endpoint: endpoint["mentor"],
        format: { key: "full_name", value: "id", mutate: "name" },
        settings: { add: false, update: false, remove: false, debounce: 1000 },
        className: `mb-[20px] min-w-full !w-full`,
        visable: true,
        isMulti: true,
      },
      {
        name: "mentors_overview",
        label: "mentor overview",
        colSpan: 12,
        type: "textarea",
        placeholder: "Enter mentor overview...",

        className: `mb-[20px] min-w-full w-full`,
        visable: (watchedMentor?.length ?? 0) > 0,
      },
      {
        type: "component",
        component: (
          <div className="flex gap-2 items-center">
            <label htmlFor="is_masterclass" className="cursor-pointer">
              Is Masterclass
            </label>
            <Controller
              control={control}
              name="is_masterclass"
              render={() => (
                <Switch
                  id="is_masterclass"
                  checked={true}
                  onCheckedChange={() => {}}
                  disabled
                />
              )}
            />
          </div>
        ),
        colSpan: 12,
        visable: pathname == "masterclass-courses" ? true : false,
      },
      {
        type: "component",
        component: (
          <div className="flex justify-between items-center">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <FormLabel label={"Show price on the website"} />
                <Controller
                  control={control}
                  name="is_price"
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={value as boolean}
                      onCheckedChange={onChange}
                    />
                  )}
                />
              </div>
              <p className="text-[12px] text-muted-foreground">
                Display only. Invoices, payments and enrollments always use the
                amount below, even when this is off.
              </p>
            </div>
            {/* {watch("is_price") && (
            <>
              <Controller
                control={control}
                name="is_enable_payment"
                render={({ field }) => (
                  <div className="flex gap-2 items-center">
                    {" "}
                    <Checkbox
                      checked={field.value === true}
                      onCheckedChange={(value: boolean) => {
                        if (value) {
                          field.onChange(true);
                        } else {
                          field.onChange(false);
                        }
                      }}
                    />
                    <FormLabel label="Enable payment" />
                  </div>
                )}
              />
            </>
          )} */}
          </div>
        ),
      },
      {
        type: "component",
        component: (
          <div className="flex items-center gap-2">
            <FormLabel label={"AI Integrated"} />
            <Controller
              control={control}
              name="is_ai_integrated"
              render={({ field: { onChange, value } }) => (
                <Switch
                  checked={value as boolean}
                  onCheckedChange={onChange}
                />
              )}
            />
          </div>
        ),
      },
      {
        name: "currency",
        label: "Choose Currency",
        colSpan: 12,
        type: "async",
        placeholder: "Select Mentor",

        option: [
          { label: "NPR", value: "NPR" },
          {
            label: "EUR",
            value: "EUR",
          },
          {
            label: "USD",
            value: "USD",
          },
          {
            label: "AUD",
            value: "AUD",
          },
        ],
        className: `mb-[20px] min-w-full !w-full`,
        // Pricing stays editable whether or not the website shows it -- these figures
        // are what invoices and payments are raised against.
        visable: true,
      },
      {
        name: "registration_fee",
        label: "Registration Fee",
        colSpan: 6,
        type: "number",
        prefix: "NPR",
        placeholder: "Enter Amount",

        className: `mb-[20px] mt-5 min-w-full w-full`,
        visable: true,
      },
      {
        name: "amount",
        label: "Total Amount",
        colSpan: 6,
        type: "number",
        prefix: "NPR",
        placeholder: "Enter Amount",

        className: `mb-[20px] mt-5 min-w-full w-full`,
        visable: true,
      },
      {
        type: "component",
        component: (
          <div className="flex items-center gap-2">
            <FormLabel label={"Would you like to include a discount?"} />
            <Controller
              control={control}
              name="is_discount"
              render={({ field: { onChange, value } }) => (
                <Switch checked={value as boolean} onCheckedChange={onChange} />
              )}
            />
          </div>
        ),
      },
      {
        name: "discount_type",
        label: "Choose Discount Type",
        colSpan: 12,
        type: "async",
        placeholder: "Select The Discount Type",

        option: [
          {
            label: "percentage",
            value: "percentage",
          },
          {
            label: "fixed",
            value: "fixed",
          },
        ],
        className: `mb-[20px] min-w-full !w-full`,
        visable: watchedIsDiscount ? true : false,
      },
      {
        name: "discount",
        label: "Discount",
        colSpan: 6,
        type: "number",

        placeholder: "Enter Discount Amount",

        className: `mb-[20px] mt-5 min-w-full w-full`,
        visable: watchedIsDiscount ? true : false,
      },
    ],
    [
      control,
      setValue,
      errors,
      watchedMentor,
      watchedIsPrice,
      watchedIsDiscount,
      pathname,
    ],
  );

  const formSchema2: FormField[] = useMemo(
    () => [
      {
        name: "header_code",
        label: "Header Code",
        colSpan: 12,
        type: "textarea",
        placeholder: "Description",

        className: `mb-[20px] min-w-full  rounded-md w-full`,
        visable: true,
      },
      {
        name: "footer_code",
        label: "Footer Code",
        colSpan: 12,
        type: "textarea",
        placeholder: "Description",

        className: `mb-[20px]  rounded-md min-w-full w-full`,
        visable: true,
      },
    ],
    [control, setValue, errors],
  );
  type ActiveProps = "images" | "upload";
  const [activeTab, setActiveTab] = useState<ActiveProps>("images");
  const [editingIconId, setEditingIconId] = useState<number | null>(null);
  const [savingIconId, setSavingIconId] = useState<number | null>(null);

  const {
    data: imagesData,
    hasNextPage,
    isFetchingNextPage,
    status,
    fetchNextPage,
    refetch,
  } = useInfiniteScroller({
    endpoint: "/custom-gallery",
    filter: {
      is_icon: true,
    },
    key: "Custom Galery",
    // enabled: true,
  });
  const { ref, inView } = useInView();
  let allScrolledData = useMemo(() => {
    return imagesData?.pages.flatMap((page: any) => page.results) || [];
  }, [imagesData]);
  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  const { mutate: deleteMultate } = usePostData();
  const handleDelete = (id: number) => {
    deleteMultate(
      {
        data,
        endpoint: endpoint["uploadimage"] + `${id}/`,
        method: METHODS.DELETE,
      },
      {
        onSuccess: () => {
          showToast({
            description: "Deleted Successfully",
            title: "Success",
            type: "success",
          });
          refetch();
        },
        onError: () => {
          showToast({
            description: "Deleted Failed",
            title: "Error",
            type: "error",
          });
        },
      },
    );
  };
  const sideformSchema: FormField[] = useMemo(
    () => [
      {
        name: "syllabus_link",
        accordian_label: "Syllabus Link",
        label: "Syllabus Link",
        colSpan: 12,
        type: "link",
        placeholder: "Enter Link",

        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "course_link",
        accordian_label: "Course Link",
        label: "Course Link",
        colSpan: 12,
        type: "link",
        placeholder: "Enter Link",

        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "feature_image",
        accordian_label: "Featured Image",
        label: "Featured Image",
        colSpan: 12,
        type: "cropper",
        placeholder: "Enter Title",

        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "cover_image",
        accordian_label: "Cover Image",
        label: "Cover Image",
        colSpan: 12,
        type: "cropper",
        placeholder: "Enter Title",

        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        accordian_label: "Feature Video",
        name: "featured_video",
        label: "Feature Video",
        colSpan: 12,
        type: "cropper",
        placeholder: "Enter Title",
        control: control,
        setValue: setValue,
        upload_type: "video",
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "excerpt",
        label: "Excerpt",
        accordian_label: "Excerpt",
        colSpan: 12,
        type: "textarea",
        placeholder: "Enter Description",

        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "categories",
        label: "Categories",
        colSpan: 12,
        accordian_label: "Categories",
        type: "async",
        isMulti: true,
        placeholder: "Select Categories",
        control: control,
        setValue: setValue,
        endpoint: endpoint["category"],
        settings: { add: true, update: true, remove: true, debounce: 1000 },
        format: {
          key: "name",
          value: "id",
          mutate: "name",
        },

        errors,
        className: `mb-[20px] min-w-full !w-full`,
        visable: true,
      },
      {
        name: "course_state",
        type: "component",
        accordian_label: "Date And Time",
        errors,
        component: (
          <>
            <FormLabel
              error={errors.course_state_type_multi?.message}
              label={"Course State"}
            ></FormLabel>
            <Controller
              name="course_state_type_multi"
              control={control}
              render={({ field: { onChange, value } }) => {
                const selected: string[] = Array.isArray(value) ? value : [];
                const toggle = (val: string) => {
                  if (selected.includes(val)) {
                    onChange(selected.filter((v) => v !== val));
                  } else {
                    onChange([...selected, val]);
                  }
                };
                return (
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selected.includes("3")}
                        onChange={() => toggle("3")}
                      />
                      All
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selected.includes("1")}
                        onChange={() => toggle("1")}
                      />
                      Ongoing
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selected.includes("2")}
                        onChange={() => toggle("2")}
                      />
                      Upcoming
                    </label>
                  </div>
                );
              }}
            />
            <br />
            {/* <Controller
            name="course_state_type"
            control={control}
            render={({ field: { onChange, value } }) => (
              <div className="flex flex-col">
                {["All", "Ongoing", "Upcoming"].map((state) => (
                  <div
                    key={state}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <Checkbox
                      label={state}
                      checked={
                        value?.toLocaleLowerCase() ===
                        state?.toLocaleLowerCase()
                      }
                      onCheckedChange={() =>
                        onChange(state?.toLocaleLowerCase())
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          /> */}
            <hr className="my-4" />
            <FormLabel label="Date & Time"></FormLabel>
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger className="w-full">
                <Button
                  endIcon={<Calendar />}
                  type="button"
                  className="w-full text-[#9ca3af] justify-between"
                  variant={"outline"}
                >
                  Select Date And Time
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                {" "}
                <Controller
                  name={"course_state"}
                  control={control}
                  render={({ field: { onChange, value, ...restFields } }) => (
                    <DateTime
                      dateTime={
                        typeof dateTimeData === "string"
                          ? JSON.parse(dateTimeData)
                          : dateTimeData
                      }
                      setDateTime={setDateTimeData}
                      value={value as any}
                      onChange={onChange}
                      open={popoverOpen}
                      setOpen={setPopoverOpen}
                      disabledPastDates={false}
                      {...restFields}
                    />
                  )}
                />
              </PopoverContent>
            </Popover>
            {typeof dateTimeData === "string"
              ? JSON.parse(dateTimeData).map((item: CourseState) => (
                  <Badge
                    className="bg-[#1da077] cursor-pointer  flex gap-2 items-center my-2 text-white rounded-full"
                    key={`${item.startTime}-${item.endTime}-${item.date}`}
                    onClick={() =>
                      setDateTimeData(
                        JSON.parse(dateTimeData).filter(
                          (i: CourseState) => i.date !== item.date,
                        ),
                      )
                    }
                  >
                    <Calendar />
                    <span className="w-[100px] truncate ">
                      {item.date
                        ? new Intl.DateTimeFormat("en-US", {
                            month: "short",
                            day: "numeric",
                          }).format(new Date(item?.date))
                        : ""}{" "}
                      {item.startTime} {item.endTime}{" "}
                    </span>

                    <X className="cursor-pointer" />
                  </Badge>
                ))
              : Array.isArray(dateTimeData) &&
                dateTimeData.length > 0 &&
                dateTimeData?.map((item: CourseState) => (
                  <Badge
                    className="bg-[#1da077] cursor-pointer  flex gap-2 items-center my-2 text-white rounded-full"
                    key={`${item.startTime}-${item.endTime}-${item.date}`}
                    onClick={() =>
                      setDateTimeData(
                        dateTimeData.filter(
                          (i: CourseState) => i.date !== item.date,
                        ),
                      )
                    }
                  >
                    <Calendar />
                    <span className="w-[100px] truncate ">
                      {item.date
                        ? new Intl.DateTimeFormat("en-US", {
                            month: "short",
                            day: "numeric",
                          }).format(new Date(item?.date))
                        : ""}{" "}
                      {item.startTime} {item.endTime}{" "}
                    </span>

                    <X className="cursor-pointer" />
                  </Badge>
                ))}
            <ErrorLabel message={errors.course_state?.message}></ErrorLabel>
            <hr className="my-4" />
            <FormLabel label="Learning Format"></FormLabel>
            <Controller
              name={"learning_format"}
              control={control}
              render={({ field: { onChange, value, ...restFields } }) => (
                <>
                  <AsyncSelect
                    onChange={onChange}
                    {...restFields}
                    placeholder="Select Course Learning Format"
                    className="w-full"
                    isMulti={true}
                    defaultValue={Array.isArray(value) ? value : []}
                    defaultOptions={[
                      {
                        label: "Physical",
                        value: "physical",
                      },
                      {
                        label: "Online",
                        value: "online",
                      },
                      {
                        label: "Hybrid",
                        value: "hybrid",
                      },
                    ]}
                    error={errors.learning_format?.message}
                  />
                </>
              )}
            />
          </>
        ),
      },

      {
        name: "duration",
        accordian_label: "Duration",
        label: "Duration",
        colSpan: 12,
        type: "async",
        endpoint: endpoint["duration"],
        format: {
          key: "duration",
          value: "id",
          mutate: "duration",
        },
        settings: {
          add: true,
        },
        placeholder: "Select Course Duration",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full !w-full`,
        visable: true,
      },
      {
        name: "meta_title",
        accordian_label: "Meta Title",
        label: "Meta Title",
        colSpan: 12,
        type: "textarea",
        placeholder: "Meta title",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full  rounded-md w-full`,
        visable: true,
      },
      {
        name: "meta_description",
        accordian_label: "Meta Description",
        label: "Meta Description",
        colSpan: 12,
        type: "textarea",
        placeholder: "Meta Description",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full  rounded-md w-full`,
        visable: true,
      },
    ],
    [
      popoverOpen,
      dateTimeData,
      register,
      watchedMentor,
      watchedIsPrice,
      watchedIsDiscount,
      allScrolledData,
      activeTab,
      editingIconId,
      savingIconId,
    ],
  );

  if (fetchPending && pageItem)
    return (
      <>
        <FormSkeleton />
      </>
    );

  return (
    <>
      <form
        id={pathname}
        onSubmit={handleSubmit(onSubmit, (err) => console.log("FORM VALIDATION ERRORS:", err))}
        className="row w-full"
      >
        <div className="col-md-8 me-auto">
          <div className="row">
            {formSchema.map(
              (field, index) =>
                (field.visable || field.visable == undefined) && (
                  <div
                    key={field.name || index}
                    className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
                  >
                    {getInputField({ ...field, control, errors, setValue })}
                  </div>
                ),
            )}
            <DragContainer
              append={append}
              control={control}
              fields={fields}
              getValues={getValues}
              move={move}
              setValue={setValue}
              watch={watch}
              key={"drag-container"}
              remove={remove}
            />
            {formSchema2.map(
              (field, index) =>
                (field.visable || field.visable == undefined) && (
                  <div
                    key={field.name || index}
                    className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
                  >
                    {getInputField({ ...field, control, errors, setValue })}
                  </div>
                ),
            )}
          </div>
        </div>
        <div className="col-md-4 col-12">
          {/* Icons accordion rendered directly — NOT through schema — so the input keeps focus */}
          <div className="col-lg-12 col-12 lg:px-[12px] px-[8px]">
            <Accordion type="multiple" defaultValue={["icons"]}>
              <AccordionItem value="icons">
                {/* @ts-ignore */}
                <AccordionTrigger
                  className="border p-4 rounded-none"
                  chevronColor="text-[#000000]"
                >
                  Icons
                </AccordionTrigger>
                <AccordionContent className={cn("p-4", "border rounded-none")}>
                  <IconsField
                    control={control}
                    allScrolledData={allScrolledData}
                    isFetchingNextPage={isFetchingNextPage}
                    scrollRef={ref}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    editingIconId={editingIconId}
                    setEditingIconId={setEditingIconId}
                    savingIconId={savingIconId}
                    setSavingIconId={setSavingIconId}
                    handleDelete={handleDelete}
                    refetch={refetch}
                    pageItem={pageItem}
                    watch={watch}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {sideformSchema.map(
            (field, index) =>
              (field.visable || field.visable == undefined) && (
                <div
                  key={field.name || index}
                  className={`col-lg-${field.colSpan}  col-12 lg:px-[12px] px-[8px]`}
                >
                  <Accordion
                    type="multiple"
                    defaultValue={[
                      "syllabus_link",
                      "course_link",
                      "cover_image",
                      "feature_image",
                      "featured_video",
                      "excerpt",
                      "categories",
                      "course_state",
                      "duration",
                      "meta_title",
                      "meta_description",
                    ]}
                  >
                    <AccordionItem value={field.name || `accordion-${index}`}>
                      {/* @ts-ignore */}
                      <AccordionTrigger
                        className="border p-4 rounded-none "
                        chevronColor="text-[#000000]"
                      >
                        {field.accordian_label}
                      </AccordionTrigger>
                      <AccordionContent
                        className={cn("p-4 ", "border rounded-none")}
                      >
                        {getInputField({ ...field, control, errors, setValue })}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              ),
          )}
        </div>
      </form>
    </>
  );
};

export default CourseForm;
